import { FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, LoaderCircle, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/client';
import { Brand } from '../components/Brand';

export function Auth() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      if (step === 'email') {
        await authApi.requestCode(email);
        setStep('code');
      } else {
        const { data } = await authApi.verifyCode(email, code);
        navigate(data.role === 'admin' ? '/admin' : '/app');
      }
    } catch {
      setError(t('auth.error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-mist px-5 py-10">
      <div className="w-full max-w-md rounded-3xl bg-white p-7 shadow-sm sm:p-10">
        <Link to="/" className="mb-12 inline-block"><Brand /></Link>
        <h1 className="display text-4xl text-ink">{step === 'email' ? t('auth.loginTitle') : t('auth.verifyTitle')}</h1>
        <p className="mt-4 text-sm leading-6 text-muted">{step === 'email' ? t('auth.emailHint') : t('auth.codeHint')}</p>
        <form onSubmit={submit} className="mt-8 space-y-4">
          {step === 'email' ? (
            <div className="flex h-14 items-center gap-2 rounded-lg border border-line px-4">
              <Mail size={19} className="text-muted" />
              <input required type="email" value={email} onChange={event => setEmail(event.target.value)} placeholder="you@example.com" className="min-w-0 flex-1 outline-none" disabled={submitting} />
            </div>
          ) : (
            <input required autoFocus inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code} onChange={event => setCode(event.target.value.replace(/\D/g, ''))} placeholder="000000" className="h-14 w-full rounded-lg border border-line px-4 text-center text-2xl tracking-[.5em] outline-none" disabled={submitting} />
          )}
          {step === 'code' && (
            <p className="text-sm leading-6 text-muted">
              {t('auth.codeHelp')}{' '}
              <a className="font-semibold text-navy underline" href="mailto:info@gougc.pro">info@gougc.pro</a>
            </p>
          )}
          <button type="submit" disabled={submitting} aria-busy={submitting} className="flex h-14 w-full items-center justify-center gap-3 rounded-lg bg-navy font-semibold text-white disabled:cursor-wait disabled:opacity-60">
            {submitting ? t('auth.waiting') : step === 'email' ? t('auth.send') : t('auth.verify')}
            {submitting ? <LoaderCircle size={18} className="animate-spin" aria-hidden="true" /> : <ArrowRight size={18} />}
          </button>
          {error && <p className="text-sm text-red-700" role="alert">{error}</p>}
        </form>
        {step === 'code' && (
          <button type="button" onClick={() => setStep('email')} disabled={submitting} className="mt-6 flex items-center gap-2 text-sm font-semibold text-navy disabled:opacity-50">
            <ArrowLeft size={16} />{t('auth.back')}
          </button>
        )}
      </div>
    </div>
  );
}
